import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { usePlan } from "./PlanContext";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const DashboardCustomizationContext = createContext();

export const useDashboardCustomization = () =>
  useContext(DashboardCustomizationContext);

export const DashboardCustomizationProvider = ({ children }) => {
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [metrics, setMetrics] = useState([]);
  const [shelfMetrics, setShelfMetrics] = useState([]);
  const [layout, setLayout] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedMetric, setDraggedMetric] = useState(null);
  const [errorState, setErrorState] = useState(null);
  // Add state to store original metrics and layout for cancel functionality
  const [originalMetrics, setOriginalMetrics] = useState([]);
  const [originalShelfMetrics, setOriginalShelfMetrics] = useState([]);
  const [originalLayout, setOriginalLayout] = useState([]);

  // Add a debounce timer ref to prevent rapid layout updates
  const layoutDebounceTimerRef = useRef(null);
  // Add a ref to track the previous layout state to avoid duplicate updates
  const previousLayoutRef = useRef("");

  const { user } = useAuth();
  const { planData } = usePlan();

  // Memoize the isPro value to avoid recalculating it repeatedly
  const isProValue = useMemo(
    () => planData?.planType === "pro",
    [planData?.planType]
  );

  // Initial metrics setup with default positions
  const defaultMetrics = [
    {
      id: "winRate",
      name: "Win Rate",
      type: "WinRateMetric",
      width: 1,
      height: 1,
      position: { x: 0, y: 0 },
    },
    {
      id: "profitFactor",
      name: "Profit Factor",
      type: "ProfitFactorMetric",
      width: 1,
      height: 1,
      position: { x: 1, y: 0 },
    },
    {
      id: "accountBalance",
      name: "Account Balance",
      type: "AccountBalanceMetric",
      width: 1,
      height: 1,
      position: { x: 2, y: 0 },
    },
    {
      id: "tradeCalendar",
      name: "Trade Calendar",
      type: "TradeCalendar",
      width: 2,
      height: 2,
      position: { x: 0, y: 1 },
    },
    {
      id: "masteryScore",
      name: "Mastery Score",
      type: "MasteryScore",
      width: 1,
      height: 2,
      position: { x: 2, y: 1 },
    },
    {
      id: "expectedReturn",
      name: "Expected Return",
      type: "ExpectedReturnMetric",
      width: 1,
      height: 1,
      position: { x: 0, y: 3 },
    },
    {
      id: "averageWin",
      name: "Average Win",
      type: "AverageWinMetric",
      width: 1,
      height: 1,
      position: { x: 1, y: 3 },
    },
    {
      id: "averageLoss",
      name: "Average Loss",
      type: "AverageLossMetric",
      width: 1,
      height: 1,
      position: { x: 2, y: 3 },
    },
  ];

  // Initialize with defaults immediately so dashboard isn't empty
  useEffect(() => {
    // Set default metrics right away
    setMetrics(defaultMetrics);

    // Create default layout
    const defaultLayout = defaultMetrics.map((metric) => ({
      i: metric.id,
      x: metric.position.x,
      y: metric.position.y,
      w: metric.width,
      h: metric.height,
    }));

    setLayout(defaultLayout);
  }, []);

  // Try to load saved layout
  useEffect(() => {
    const loadCustomization = async () => {
      if (!user || !isProValue) {
        return;
      }

      try {
        const customizationRef = doc(db, "dashboardCustomization", user.uid);
        const docSnap = await getDoc(customizationRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          // Only update if we have valid metrics
          if (data.metrics && data.metrics.length > 0) {
            setMetrics(data.metrics);
          }

          // Only update if we have valid shelf metrics
          if (data.shelfMetrics) {
            setShelfMetrics(data.shelfMetrics || []);
          }

          // Only update if we have valid layout
          if (data.layout && data.layout.length > 0) {
            setLayout(data.layout);
          }
        } else {
          // Default initialization was already done in first useEffect
        }
      } catch (error) {
        console.error("Error loading dashboard customization:", error);
        setErrorState(error.message);

        // On error, ensure we have default metrics
        setMetrics(defaultMetrics);

        // Create default layout
        const defaultLayout = defaultMetrics.map((metric) => ({
          i: metric.id,
          x: metric.position.x,
          y: metric.position.y,
          w: metric.width,
          h: metric.height,
        }));

        setLayout(defaultLayout);
      }
    };

    loadCustomization();
  }, [user, isProValue]);

  // Save customization to Firestore
  const saveCustomization = async () => {
    if (!user || !isProValue) {
      console.error(
        "Cannot save customization - user or Pro status check failed"
      );
      return false;
    }

    try {
      const customizationRef = doc(db, "dashboardCustomization", user.uid);
      await setDoc(customizationRef, {
        metrics,
        shelfMetrics,
        layout,
        updatedAt: new Date(),
      });
      return true;
    } catch (error) {
      console.error("Error saving dashboard customization:", error);
      setErrorState(error.message);
      return false;
    }
  };

  // Enter customization mode
  const enterCustomizationMode = () => {
    if (!isProValue) {
      return;
    }
    // Store original state when entering customization mode
    setOriginalMetrics(JSON.parse(JSON.stringify(metrics)));
    setOriginalShelfMetrics(JSON.parse(JSON.stringify(shelfMetrics)));
    setOriginalLayout(JSON.parse(JSON.stringify(layout)));
    setIsCustomizing(true);
  };

  // Exit customization mode with metric updates
  const exitCustomizationMode = async (save = false) => {
    if (save) {
      try {
        // Update metrics positions based on current layout before saving
        const updatedMetrics = metrics.map((metric) => {
          const layoutItem = layout.find((item) => item.i === metric.id);
          if (layoutItem) {
            return {
              id: metric.id,
              name: metric.name,
              type: metric.type,
              width: layoutItem.w,
              height: layoutItem.h,
              position: { x: layoutItem.x, y: layoutItem.y },
            };
          }
          return {
            id: metric.id,
            name: metric.name,
            type: metric.type,
            width: metric.width || 1,
            height: metric.height || 1,
            position: metric.position || { x: 0, y: 0 },
          };
        });

        // Clean shelf metrics for Firebase storage
        const cleanShelfMetrics = (shelfMetrics || []).map((metric) => ({
          id: metric.id,
          name: metric.name,
          type: metric.type,
          width: metric.width || 1,
          height: metric.height || 1,
          position: metric.position || { x: 0, y: 0 },
        }));

        // Clean layout for Firebase storage
        const cleanLayout = (layout || []).map((item) => ({
          i: item.i,
          x: item.x || 0,
          y: item.y || 0,
          w: item.w || 1,
          h: item.h || 1,
        }));

        // Create a clean object with only serializable data
        const dataToSave = {
          metrics: updatedMetrics,
          shelfMetrics: cleanShelfMetrics,
          layout: cleanLayout,
          updatedAt: new Date(),
        };

        // Update metrics state with cleaned data
        setMetrics(updatedMetrics);

        // Save to Firestore
        const customizationRef = doc(db, "dashboardCustomization", user.uid);
        await setDoc(customizationRef, dataToSave);

        setIsCustomizing(false);
        return true;
      } catch (error) {
        console.error("Error saving dashboard customization:", error);
        setErrorState(error.message);
        return false;
      }
    } else {
      // Revert to original state when canceling
      setMetrics(originalMetrics);
      setShelfMetrics(originalShelfMetrics);
      setLayout(originalLayout);
      setIsCustomizing(false);
      return true;
    }
  };

  // Add metric to dashboard from shelf
  const addMetricToDashboard = (metricId) => {
    const metric = shelfMetrics.find((m) => m.id === metricId);
    if (!metric) return;

    // Find the first available position on the dashboard
    const newPosition = findAvailablePosition(metrics);

    // Add metric to dashboard
    const newMetric = {
      ...metric,
      position: newPosition,
    };

    setMetrics([...metrics, newMetric]);

    // Remove from shelf
    setShelfMetrics(shelfMetrics.filter((m) => m.id !== metricId));

    // Update layout
    setLayout([
      ...layout,
      {
        i: metric.id,
        x: newPosition.x,
        y: newPosition.y,
        w: metric.width,
        h: metric.height,
      },
    ]);
  };

  // Move metric to shelf
  const moveMetricToShelf = (metricId) => {
    const metric = metrics.find((m) => m.id === metricId);
    if (!metric) return;

    // Add to shelf
    setShelfMetrics([...shelfMetrics, metric]);

    // Remove from dashboard
    setMetrics(metrics.filter((m) => m.id !== metricId));

    // Update layout
    setLayout(layout.filter((item) => item.i !== metricId));
  };

  // Resize metric
  const resizeMetric = (metricId, newSize) => {
    setMetrics(
      metrics.map((metric) =>
        metric.id === metricId
          ? { ...metric, width: newSize.width, height: newSize.height }
          : metric
      )
    );

    setLayout(
      layout.map((item) =>
        item.i === metricId
          ? { ...item, w: newSize.width, h: newSize.height }
          : item
      )
    );
  };

  // Move metric
  const moveMetric = (metricId, newPosition) => {
    setMetrics(
      metrics.map((metric) =>
        metric.id === metricId ? { ...metric, position: newPosition } : metric
      )
    );

    setLayout(
      layout.map((item) =>
        item.i === metricId
          ? { ...item, x: newPosition.x, y: newPosition.y }
          : item
      )
    );
  };

  // Handle layout change with debouncing
  const onLayoutChange = useCallback((newLayout) => {
    // Convert layout to string for comparison
    const layoutString = JSON.stringify(newLayout);

    // Don't update if layout hasn't changed
    if (previousLayoutRef.current === layoutString) {
      return;
    }

    // Update previous layout ref
    previousLayoutRef.current = layoutString;

    // Clear any pending debounce timer
    if (layoutDebounceTimerRef.current) {
      clearTimeout(layoutDebounceTimerRef.current);
    }

    // Set a new debounce timer
    layoutDebounceTimerRef.current = setTimeout(() => {
      setLayout(newLayout);
      layoutDebounceTimerRef.current = null;
    }, 50); // 50ms debounce
  }, []);

  // Helper function to find available position for a new metric
  const findAvailablePosition = (currentMetrics) => {
    // Default to position 0,0
    let position = { x: 0, y: 0 };

    // Find the lowest available y position
    if (currentMetrics.length > 0) {
      const maxY = Math.max(
        ...currentMetrics.map((m) => m.position.y + m.height)
      );
      position = { x: 0, y: maxY };
    }

    return position;
  };

  // Use a stable function that returns the memoized value
  const isPro = useCallback(() => isProValue, [isProValue]);

  const value = {
    isCustomizing,
    metrics,
    shelfMetrics,
    layout,
    isDragging,
    draggedMetric,
    errorState,
    isPro,
    enterCustomizationMode,
    exitCustomizationMode,
    addMetricToDashboard,
    moveMetricToShelf,
    resizeMetric,
    moveMetric,
    onLayoutChange,
    setIsDragging,
    setDraggedMetric,
  };

  return (
    <DashboardCustomizationContext.Provider value={value}>
      {children}
    </DashboardCustomizationContext.Provider>
  );
};
